import React, { useEffect, useMemo, useState } from 'react';
import { AndroidOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, DotLoading } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LocaleSwitcher } from '../../../components/LocaleSwitcher';
import { androidAppApi, AndroidApkManifest } from '../../../api/android-app';
import { getApiOrigin } from '../../../config/api';
import {
  APK_FILE_NAME,
  APP_ICON_URL,
  downloadPageStyles,
} from './styles';
import { useDownloadDefaultLocale } from '../useDownloadDefaultLocale';

export const DownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('download');
  useDownloadDefaultLocale();
  const [latestApk, setLatestApk] = useState<AndroidApkManifest | null>(null);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadLatest = async () => {
      setLoadingLatest(true);
      setLoadError('');
      try {
        const response = await androidAppApi.getLatestApk();
        if (mounted) {
          setLatestApk(response.data.data);
        }
      } catch {
        if (mounted) {
          setLatestApk(null);
          setLoadError(t('download.unavailable'));
        }
      } finally {
        if (mounted) {
          setLoadingLatest(false);
        }
      }
    };

    void loadLatest();

    return () => {
      mounted = false;
    };
  }, [t]);

  const downloadUrl = useMemo(
    () => latestApk?.download_url ? resolveDownloadUrl(latestApk.download_url) : '',
    [latestApk]
  );

  return (
    <main style={downloadPageStyles.page}>
      <div style={downloadPageStyles.shell}>
        <header style={downloadPageStyles.header}>
          <div style={downloadPageStyles.versionBadge}>{t('download.badge')}</div>
          <LocaleSwitcher
            tone="light"
            labelMode="short"
            style={downloadPageStyles.localeSwitcher}
          />
        </header>

        <section style={downloadPageStyles.hero}>
          <img src={APP_ICON_URL} alt={t('download.iconAlt')} style={downloadPageStyles.icon} />
          <h1 style={downloadPageStyles.title}>{t('download.title')}</h1>
          <div style={downloadPageStyles.tagline}>{t('download.tagline')}</div>
          <div style={downloadPageStyles.subtitle}>{t('download.subtitle')}</div>
        </section>

        <div style={downloadPageStyles.actionStack}>
          {downloadUrl ? (
            <a href={downloadUrl} download={latestApk?.original_name || APK_FILE_NAME} style={{ textDecoration: 'none' }}>
              <Button block color="warning" style={downloadPageStyles.primaryButton}>
                <DownloadOutlined style={{ marginRight: 8 }} />
                {t('download.downloadButton')}
              </Button>
            </a>
          ) : (
            <Button block color="warning" disabled loading={loadingLatest} style={downloadPageStyles.primaryButton}>
              {loadingLatest ? (
                <>
                  <DotLoading />
                  <span style={{ marginLeft: 8 }}>{t('download.loadingLatest')}</span>
                </>
              ) : (
                <>
                  <DownloadOutlined style={{ marginRight: 8 }} />
                  {t('download.downloadButton')}
                </>
              )}
            </Button>
          )}

          <Button block fill="none" style={downloadPageStyles.secondaryButton} onClick={() => navigate('/download/detail')}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            {t('download.detailButton')}
          </Button>
        </div>

        <section style={downloadPageStyles.infoGrid} aria-label={t('download.infoAriaLabel')}>
          <div style={downloadPageStyles.infoItem}>
            <div style={downloadPageStyles.infoLabel}>{t('download.platformLabel')}</div>
            <div style={downloadPageStyles.infoValue}>
              <AndroidOutlined style={{ marginRight: 4 }} />
              {t('download.platformValue')}
            </div>
          </div>
          <div style={downloadPageStyles.infoItem}>
            <div style={downloadPageStyles.infoLabel}>{t('download.versionLabel')}</div>
            <div style={downloadPageStyles.infoValue}>{latestApk?.version_name || t('download.versionFallback')}</div>
          </div>
          <div style={downloadPageStyles.infoItem}>
            <div style={downloadPageStyles.infoLabel}>{t('download.sizeLabel')}</div>
            <div style={downloadPageStyles.infoValue}>{latestApk ? formatBytes(latestApk.size) : '--'}</div>
          </div>
        </section>

        {latestApk?.uploaded_at ? (
          <div style={downloadPageStyles.notice}>
            {t('download.latestUpdatedAt', { time: new Date(latestApk.uploaded_at).toLocaleString() })}
          </div>
        ) : null}
        {loadError ? <div style={downloadPageStyles.errorNotice}>{loadError}</div> : null}
        <div style={downloadPageStyles.notice}>{t('download.notice')}</div>
        <div style={downloadPageStyles.privacyNotice}>{t('download.privacyNotice')}</div>
      </div>
    </main>
  );
};

function resolveDownloadUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const origin = getApiOrigin() || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) {
    return url;
  }
  return new URL(url, origin).toString();
}

function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}
